/**
 * frontend/src/components/ClubJoinModal.jsx
 * Bottom-sheet modal nudging the user to join PriceMap Club for 2 GEL/month.
 *
 * The "გაწევრიანება" button is intentionally disabled with a "მალე" badge
 * until Phase 3 (payment integration) ships.
 */
import React, { useEffect } from 'react';
import { Crown, Check, X } from 'lucide-react';

export default function ClubJoinModal({ open, onClose }) {
  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/55 flex items-end sm:items-center justify-center p-3 sm:p-6"
      onClick={onClose}
      data-testid="club-join-modal"
    >
      <div
        className="w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl p-5 pb-6 relative shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close X */}
        <button
          onClick={onClose}
          aria-label="დახურვა"
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center active:scale-95 transition-all"
        >
          <X className="w-4 h-4 text-slate-600" strokeWidth={2.2} />
        </button>

        {/* Crown icon */}
        <div className="flex justify-center mb-3">
          <div className="w-14 h-14 bg-violet-100 rounded-full flex items-center justify-center">
            <Crown className="w-7 h-7 text-violet-700" strokeWidth={1.8} />
          </div>
        </div>

        <h3 className="text-center text-[17px] font-semibold text-slate-900 mb-1">
          PriceMap Club
        </h3>
        <p className="text-center text-[13px] text-slate-500 leading-snug mb-4">
          მრავალჯერადი ვაუჩერების მისაღებად საჭიროა Club წევრობა
        </p>

        {/* Plan box */}
        <div className="bg-slate-50 rounded-2xl px-4 py-3.5 mb-5">
          <div className="flex items-baseline justify-center gap-1 mb-2.5">
            <span className="text-[28px] font-bold text-violet-700 leading-none">2₾</span>
            <span className="text-[12px] text-slate-400">/ თვეში</span>
          </div>
          <ul className="text-[12px] text-slate-600 space-y-1.5">
            <li className="flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" strokeWidth={2.5} />
              <span>ულიმიტო მრავალჯერადი ვაუჩერები</span>
            </li>
            <li className="flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" strokeWidth={2.5} />
              <span>Cooldown-ის გარეშე</span>
            </li>
            <li className="flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" strokeWidth={2.5} />
              <span>ექსკლუზიური Club-შეთავაზებები</span>
            </li>
          </ul>
        </div>

        {/* Primary CTA — disabled until Phase 3 (payment) */}
        <div className="relative">
          <button
            disabled
            className="w-full bg-violet-600 text-white text-[14px] font-semibold py-3 rounded-2xl opacity-60 cursor-not-allowed mb-2"
            data-testid="club-join-cta"
          >
            გაწევრიანება — 2₾
          </button>
          <span className="absolute -top-2 right-3 bg-amber-300 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded-full">
            მალე
          </span>
        </div>

        <button
          onClick={onClose}
          className="w-full text-slate-500 text-[13px] py-2 hover:text-slate-700 transition-colors"
        >
          არა, მადლობა
        </button>
      </div>
    </div>
  );
}
